// ============================================
// Axios API Service — Role-Aware Token Handling
// ============================================

import axios from 'axios';
import { API_BASE_URL } from '../constants';

// ── Helper: pick the correct token based on current URL path ──
const getTokenForCurrentPath = () => {
    const path = window.location.pathname;
    if (path.startsWith('/admin')) return localStorage.getItem('admin_token');
    if (path.startsWith('/dispatcher')) return localStorage.getItem('dispatcher_token');
    if (path.startsWith('/driver')) return localStorage.getItem('driver_token');
    if (path.startsWith('/pa')) return localStorage.getItem('pa_token');
    return localStorage.getItem('token'); // user/default
};

const getRefreshTokenForCurrentPath = () => {
    const path = window.location.pathname;
    if (path.startsWith('/admin')) return localStorage.getItem('admin_refreshToken');
    if (path.startsWith('/dispatcher')) return localStorage.getItem('dispatcher_refreshToken');
    if (path.startsWith('/driver')) return localStorage.getItem('driver_refreshToken');
    if (path.startsWith('/pa')) return localStorage.getItem('pa_refreshToken');
    return localStorage.getItem('refreshToken');
};

const getLoginPathForCurrentPath = () => {
    const path = window.location.pathname;
    if (path.startsWith('/admin')) return '/admin/login';
    if (path.startsWith('/dispatcher')) return '/dispatcher/login';
    if (path.startsWith('/driver')) return '/driver/login';
    if (path.startsWith('/pa')) return '/login';
    return '/login';
};

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 15000,
});

// ── Request Interceptor ─────────────────────
api.interceptors.request.use(
    (config) => {
        const token = getTokenForCurrentPath();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ── Response Interceptor ────────────────────
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Identify public/login pages where we should NOT force-redirect
        const currentPath = window.location.pathname;
        const isPublicPage = (
            currentPath === '/login' ||
            currentPath === '/driver/login' ||
            currentPath === '/admin/login' ||
            currentPath === '/dispatcher/login' ||
            currentPath.includes('/forgot-password') ||
            currentPath.includes('/reset-password') ||
            currentPath.includes('/register') ||
            currentPath === '/'
        );

        const isAuthEndpoint = originalRequest.url.includes('/auth/login') || originalRequest.url.includes('/auth/refresh') || originalRequest.url.includes('/auth/google-login');

        // Handle 401 — attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            originalRequest._retry = true;

            try {
                const refreshToken = getRefreshTokenForCurrentPath();
                if (!refreshToken) throw new Error('Logout in progress');

                const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                    refreshToken,
                });

                // Store refreshed token
                const path = window.location.pathname;
                let prefix = '';
                if (path.startsWith('/admin')) prefix = 'admin';
                else if (path.startsWith('/dispatcher')) prefix = 'dispatcher';
                else if (path.startsWith('/driver')) prefix = 'driver';
                else if (path.startsWith('/pa')) prefix = 'pa';

                const tokenKey = prefix ? `${prefix}_token` : 'token';
                localStorage.setItem(tokenKey, data.data.token);

                // Retry original request
                originalRequest.headers.Authorization = `Bearer ${data.data.token}`;
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh failed or token still invalid — clear and redirect
                const path = window.location.pathname;
                if (path.startsWith('/admin')) {
                    localStorage.removeItem('admin_token');
                    localStorage.removeItem('admin_refreshToken');
                } else if (path.startsWith('/dispatcher')) {
                    localStorage.removeItem('dispatcher_token');
                    localStorage.removeItem('dispatcher_refreshToken');
                } else if (path.startsWith('/driver')) {
                    localStorage.removeItem('driver_token');
                    localStorage.removeItem('driver_refreshToken');
                } else if (path.startsWith('/pa')) {
                    localStorage.removeItem('pa_token');
                    localStorage.removeItem('pa_refreshToken');
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                }
                
                // Only force hard redirect if it was a genuine session expiry 
                // (i.e., not a race condition during a legitimate user-initiated logout where refresh token is null)
                if (refreshError.message !== 'Logout in progress') {
                    window.dispatchEvent(new CustomEvent('auth:required', { detail: { path: getLoginPathForCurrentPath() } }));
                }
                
                return Promise.reject(refreshError);
            }
        }

        // Handle 403 — Account Blocked (Step 72)
        // ONLY redirect if user is on a PROTECTED route (dashboard).
        // If already on login/public page, let the error propagate naturally
        // so the UI can show the "Contact Admin" form (Step 71).
        if (error.response?.status === 403) {
            const msg = error.response?.data?.message || '';
            if (msg.toLowerCase().includes('block') && !isPublicPage) {
                const path = window.location.pathname;
                if (path.startsWith('/admin')) {
                    localStorage.removeItem('admin_token');
                    localStorage.removeItem('admin_refreshToken');
                } else if (path.startsWith('/dispatcher')) {
                    localStorage.removeItem('dispatcher_token');
                    localStorage.removeItem('dispatcher_refreshToken');
                } else if (path.startsWith('/driver')) {
                    localStorage.removeItem('driver_token');
                    localStorage.removeItem('driver_refreshToken');
                } else if (path.startsWith('/pa')) {
                    localStorage.removeItem('pa_token');
                    localStorage.removeItem('pa_refreshToken');
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                }
                localStorage.removeItem('user');
                window.dispatchEvent(new CustomEvent('auth:required', { detail: { path: getLoginPathForCurrentPath() } }));
                return Promise.reject(error);
            }
            // On public/login pages: just let the error propagate — UI handles it
        }

        // Note: Second 401 retry case is already handled above in the catch block.
        // No additional hard redirect needed here.

        return Promise.reject(error);
    }
);

export default api;
